export const currency = (n, code = "USD") => {
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: code, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${code} ${v.toLocaleString()}`;
  }
};

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export const relativeTime = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return fmtDate(iso);
};

// 24-stage IPMP pipeline grouped into phases for color coding
export const PIPELINE_STAGES = [
  { id: "new_lead",                  label: "New Lead",                phase: "Acquisition", color: "#5C6660", bg: "#F2F4F3" },
  { id: "coordinator_assigned",      label: "Coordinator Assigned",    phase: "Acquisition", color: "#5C6660", bg: "#F2F4F3" },
  { id: "contact_attempted",         label: "Contact Attempted",       phase: "Acquisition", color: "#5C6660", bg: "#F2F4F3" },
  { id: "reports_requested",         label: "Reports Requested",       phase: "Discovery",   color: "#2980B9", bg: "#EAF2F8" },
  { id: "reports_received",          label: "Reports Received",        phase: "Discovery",   color: "#2980B9", bg: "#EAF2F8" },
  { id: "medical_review_requested",  label: "Medical Review Requested", phase: "Discovery",  color: "#2980B9", bg: "#EAF2F8" },
  { id: "doctor_opinion_received",   label: "Doctor Opinion Received", phase: "Discovery",   color: "#2980B9", bg: "#EAF2F8" },
  { id: "estimate_draft",            label: "Estimate Draft",          phase: "Quotation",   color: "#B26B00", bg: "#FFF4E5" },
  { id: "estimate_sent",             label: "Estimate Sent",           phase: "Quotation",   color: "#B26B00", bg: "#FFF4E5" },
  { id: "decision_pending",          label: "Decision Pending",        phase: "Quotation",   color: "#B26B00", bg: "#FFF4E5" },
  { id: "treatment_accepted",        label: "Treatment Accepted",      phase: "Quotation",   color: "#B26B00", bg: "#FFF4E5" },
  { id: "visa_assistance",           label: "Visa Assistance",         phase: "Travel",      color: "#7A5AC2", bg: "#EFE9FB" },
  { id: "visa_approved",             label: "Visa Approved",           phase: "Travel",      color: "#7A5AC2", bg: "#EFE9FB" },
  { id: "travel_planning",           label: "Travel Planning",         phase: "Travel",      color: "#7A5AC2", bg: "#EFE9FB" },
  { id: "airport_pickup_scheduled",  label: "Airport Pickup",          phase: "Travel",      color: "#7A5AC2", bg: "#EFE9FB" },
  { id: "admission_confirmed",       label: "Admission Confirmed",     phase: "Treatment",   color: "#2A4D3E", bg: "#E6F0EB" },
  { id: "treatment_in_progress",     label: "Treatment In Progress",   phase: "Treatment",   color: "#2A4D3E", bg: "#E6F0EB" },
  { id: "icu",                       label: "ICU",                     phase: "Treatment",   color: "#C0392B", bg: "#FDEDEC" },
  { id: "recovery",                  label: "Recovery",                phase: "Treatment",   color: "#2A4D3E", bg: "#E6F0EB" },
  { id: "discharge",                 label: "Discharge",               phase: "Treatment",   color: "#2A4D3E", bg: "#E6F0EB" },
  { id: "hotel_stay",                label: "Hotel Stay",              phase: "Closure",     color: "#D97757", bg: "#FBEDE5" },
  { id: "return_journey",            label: "Return Journey",          phase: "Closure",     color: "#D97757", bg: "#FBEDE5" },
  { id: "online_followup",           label: "Online Follow-up",        phase: "Closure",     color: "#D97757", bg: "#FBEDE5" },
  { id: "closed",                    label: "Closed",                  phase: "Closure",     color: "#D97757", bg: "#FBEDE5" },
];

export const STAGE_BY_ID = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.id, s]));
export const PHASES = ["Acquisition", "Discovery", "Quotation", "Travel", "Treatment", "Closure"];
export const PHASE_COLORS = {
  Acquisition: "#5C6660",
  Discovery: "#2980B9",
  Quotation: "#B26B00",
  Travel: "#7A5AC2",
  Treatment: "#2A4D3E",
  Closure: "#D97757",
};

export const COMMISSION_STATUSES = [
  { id: "pending",  label: "Pending",  color: "#B26B00", bg: "#FFF4E5" },
  { id: "approved", label: "Approved", color: "#2980B9", bg: "#EAF2F8" },
  { id: "paid",     label: "Paid",     color: "#2A4D3E", bg: "#E6F0EB" },
];

export const QUOTATION_STATUSES = [
  { id: "draft",    label: "Draft",    color: "#5C6660", bg: "#F2F4F3" },
  { id: "sent",     label: "Sent",     color: "#2980B9", bg: "#EAF2F8" },
  { id: "accepted", label: "Accepted", color: "#2A4D3E", bg: "#E6F0EB" },
  { id: "rejected", label: "Rejected", color: "#C0392B", bg: "#FDEDEC" },
];

export const countryFlag = (country) => {
  const map = {
    UAE: "🇦🇪", "United Arab Emirates": "🇦🇪",
    Nigeria: "🇳🇬", Bangladesh: "🇧🇩", Kenya: "🇰🇪",
    Oman: "🇴🇲", Iraq: "🇮🇶", Ethiopia: "🇪🇹", Sudan: "🇸🇩",
    India: "🇮🇳", USA: "🇺🇸", UK: "🇬🇧",
  };
  return map[country] || "🌍";
};
